/**
 * Walk 4 (#2268) — the staff actions on one onboarding file: the buttons beside each open item,
 * the "Record the look-through" dialog, the small confirms, and the result toast. Card 2404 adds
 * an action on every item: the decisions on a person (View result, Clear, Ask them to re-run,
 * Confirm removal, Restore, Add email), each a dialog in the approved ResolveDialog pattern.
 *
 * <h3>Who may act is the ENGINE's answer, never this bundle's</h3>
 * <p>Every action reference on an open item carries `allowed`, computed on the server for this
 * viewer from the tenant's role grants (the role tokens are declared in the workflow, and the
 * route checks them again on the POST). A button is drawn ONLY when `allowed` is true; a viewer
 * with no role grant sees every item and no buttons. Nothing here names a role.</p>
 *
 * <h3>Every action</h3>
 * <ul>
 *   <li><b>Send reminder</b> — re-sends the owed form or link now. The click IS the confirmation
 *       (contract decision 2), so it posts at once. The body IS the reference's `target`, as the
 *       engine sent it: `{ party_key }` for a person's form, `{ item_key }` for something the
 *       client owes; a reference that names neither sends to everyone owed ("Send reminders",
 *       `{ all: true }`).</li>
 *   <li><b>Record look-through</b> — the design's dialog: the people who own or control the
 *       company and their band, how it was confirmed, notes, and whether to send them identity
 *       checks. Saving clears the approval hold.</li>
 *   <li><b>Mark received</b> — a small confirm, then settles the item.</li>
 *   <li><b>Start risk assessment / Review and approve</b> — links to the existing staff forms
 *       (the reference's `url`); nothing is posted from here.</li>
 *   <li><b>View result</b> (card 2404) — the person's identity-check detail and screening hits, from
 *       the file as last read; nothing is posted.</li>
 *   <li><b>Clear</b> — a REQUIRED reason (the button stays off until there is one), then
 *       `clear-party { party_key, reason }`.</li>
 *   <li><b>Ask them to re-run</b> — a confirm, then `rerun-identity-check { party_key }`.</li>
 *   <li><b>Confirm removal</b> — lists the people the client removed and posts
 *       `confirm-removal { reason? }` (the engine reads who; the page never sends a list).</li>
 *   <li><b>Restore</b> — one button for the grouped item; pick the person, then
 *       `restore-party { party_key }`.</li>
 *   <li><b>Add email</b> — one checked address, then `add-email { party_key, email }`, which sends
 *       their form.</li>
 * </ul>
 * <p>Each result is a toast. A 403 says who CAN ("Only a@x or b@y can do this") — inside the
 * dialog when one is open, so the names stay on screen; a 409 says "Already done"; and the file is
 * refetched after every answer so the page shows what is true. "Send reminders" on the panel
 * reminds everyone owed at once (`{ all: true }`) when two or more open items can be chased.</p>
 *
 * <h3>Deep links</h3>
 * <p>`/onboarding/:jobId?action=<action>&party=<key>` (the Google Chat alerts link here) opens that
 * action's dialog once the file has loaded — only when this viewer may take it. A reminder or a
 * form link opened this way asks first, because nobody has clicked anything yet. A link whose item
 * is not on the file says it is looking — the item may not be open yet, or may already have been
 * dealt with — and re-reads the file every 15 seconds for up to two minutes.</p>
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Icon, Input, Modal, Select, Textarea, Toast } from "@ardept/design-system";
import {
  addEmail,
  clearParty,
  confirmRemoval,
  describeActionError,
  markReceived,
  recordLookThrough,
  rerunIdentityCheck,
  restoreParty,
  sendReminder,
  useJobAction,
} from "../api/portalQueries.js";
import type {
  JobStatusActionName,
  JobStatusActionRef,
  JobStatusDetailResponse,
  JobStatusOpenItem,
  JobStatusOpenItemPerson,
  JobStatusPerson,
  LookThroughBand,
  LookThroughConfirmedVia,
  MarkReceivedRequest,
  RecordLookThroughRequest,
  RecordLookThroughResponse,
  SendReminderRequest,
  SendReminderResponse,
} from "../api/types.js";
import { PersonStatus, hitLine } from "./personStatus.js";

// ── Words ────────────────────────────────────────────────────────────────────────────────

/** The bands, as the design's select says them, in the contract's values. */
export const BAND_OPTIONS: readonly { value: LookThroughBand; label: string }[] = [
  { value: "25_50", label: "25–50%" },
  { value: "50_75", label: "50–75%" },
  { value: "75_plus", label: "75% or more" },
  { value: "other_control", label: "Other control" },
];

/** How it was confirmed, as the design's select says it, in the contract's values. */
export const CONFIRMED_VIA_OPTIONS: readonly { value: LookThroughConfirmedVia; label: string }[] = [
  { value: "companies_house", label: "The company's Companies House register" },
  { value: "client_documents", label: "The client told us, with documents" },
  { value: "group_structure_chart", label: "Group structure chart from the client" },
];

/** An unresolved deep link: what the page says while it waits, how often it re-reads, how long. */
export const DEEP_LINK_PENDING =
  "Looking for this item on the file — it may already have been dealt with. This page re-checks for a couple of minutes.";
export const DEEP_LINK_GAVE_UP =
  "That item has not appeared on this file. It may already have been dealt with — refresh to check.";
export const DEEP_LINK_POLL_MS = 15_000;
// Two minutes: the engine opens the item on the same pass that decides it is owed, so a link that
// is still unresolved after that is almost always an item someone already dealt with.
export const DEEP_LINK_WAIT_MS = 2 * 60_000;

/** How long an error toast stays up: long enough to read who CAN do something. */
const ERROR_TOAST_MS = 12_000;

/** The two staff-form actions, which are links to the existing forms rather than posts. */
const FORM_LINK_LABEL: Partial<Record<JobStatusActionName, string>> = {
  start_risk_assessment: "Start risk assessment",
  review_and_approve: "Review and approve",
};

/**
 * Card 2404 — the buttons for the decisions on a person, in the card's words. Each opens a dialog:
 * nothing here posts on the click itself.
 */
export const PERSON_ACTION_LABEL: Readonly<Partial<Record<JobStatusActionName, string>>> = {
  view_result: "View result",
  clear_party: "Clear",
  rerun_identity_check: "Ask them to re-run",
  confirm_removal: "Confirm removal",
  restore_party: "Restore",
  add_email: "Add email",
};

/** The actions that act on ONE person, and so need a party key to post. */
const NEEDS_PARTY: ReadonlySet<JobStatusActionName> = new Set([
  "view_result",
  "clear_party",
  "rerun_identity_check",
  "add_email",
]);

/** A usable email address: something@something.something, no spaces. The engine checks it again. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** True when `value` looks like an email address a form could be sent to. */
export function looksLikeEmail(value: string): boolean {
  return EMAIL_SHAPE.test(value.trim());
}

/** The person an action is about: the reference's own party, else the item's owner. */
function partyOf(item: JobStatusOpenItem, ref: JobStatusActionRef): string | null {
  return ref.target.party_key ?? item.owed_by.party_key ?? null;
}

/**
 * A person's full name, from the richest source the file holds: the people table, the party
 * ledger, then the grouped item's own list. Never a party key.
 */
export function personName(
  detail: JobStatusDetailResponse | undefined,
  partyKey: string | null,
  item?: JobStatusOpenItem,
): string {
  if (partyKey === null) return "this person";
  const name =
    detail?.people?.find((person) => person.party_key === partyKey)?.name ??
    detail?.parties.find((party) => party.iteration_key === partyKey)?.display_name ??
    item?.people?.find((person) => person.party_key === partyKey)?.name;
  return typeof name === "string" && name.trim() !== "" ? name : "this person";
}

/** "a", "a and b", "a, b and c". */
function joinAnd(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/**
 * The exact body for a reminder: the reference's own `target`, never re-derived here. The engine
 * resolves `party_key` / `item_key` against the SAME reminder plan it offered the button from, so
 * posting anything else (a guessed party, or `{ all: true }` for one client item) would remind
 * people the staffer did not click on.
 */
export function reminderBody(ref: JobStatusActionRef): SendReminderRequest {
  if (ref.target.party_key !== undefined) return { party_key: ref.target.party_key };
  if (ref.target.item_key !== undefined) return { item_key: ref.target.item_key };
  return { all: true };
}

/** A reminder aimed at one person or one item, rather than at everyone owed. */
function isSingleReminder(ref: JobStatusActionRef): boolean {
  return ref.target.party_key !== undefined || ref.target.item_key !== undefined;
}

/** What a reminder result says. */
export function reminderResult(result: SendReminderResponse): { message: string; ok: boolean } {
  const names = result.sent.map((entry) => entry.name);
  const missed = result.skipped
    .map((entry) => entry.name)
    .filter((name) => typeof name === "string" && name.trim() !== "");
  if (names.length === 0) {
    if (result.skipped.length === 0) {
      return { message: "No reminder was sent: nobody is waiting to be reminded.", ok: false };
    }
    // One skip carries the engine's own reason ("They don't owe anything.", "No email address…").
    const only = result.skipped.length === 1 ? result.skipped[0] : undefined;
    return {
      message:
        only !== undefined && missed.length === 1
          ? `No reminder was sent to ${missed[0]}. ${only.reason}`
          : missed.length > 0
            ? `No reminder was sent: ${joinAnd(missed)} could not be reached.`
            : "No reminder was sent: nobody on this item can be reached yet.",
      ok: false,
    };
  }
  const head = names.length === 1 ? `Reminder sent to ${names[0]}` : `Reminders sent to ${joinAnd(names)}`;
  const skippedCount = result.skipped.length;
  const tail =
    skippedCount === 0
      ? "."
      : missed.length === skippedCount
        ? `. Could not reach ${joinAnd(missed)}.`
        : `. ${skippedCount} ${skippedCount === 1 ? "person" : "people"} could not be reached.`;
  return { message: `${head}${tail}`, ok: true };
}

/**
 * What a recorded look-through says. The added people whose identity-form email failed
 * (`link_failed`, by party key) are named, not counted as sent: their forms are still owed and a
 * staffer has to reach them another way.
 */
export function lookThroughResult(result: RecordLookThroughResponse): string {
  const failedKeys = new Set(result.link_failed ?? []);
  const sent = result.added_parties.filter((party) => !failedKeys.has(party.party_key));
  const failed = result.added_parties.filter((party) => failedKeys.has(party.party_key));
  // A failed key the engine did not also list as added still counts, by number.
  const unnamed = failedKeys.size - failed.length;
  let message = "Look-through recorded. The approval hold is cleared.";
  if (sent.length > 0) message += ` Identity checks sent to ${joinAnd(sent.map((p) => p.name))}.`;
  if (failed.length > 0 || unnamed > 0) {
    const who =
      unnamed === 0
        ? joinAnd(failed.map((p) => p.name))
        : `${failedKeys.size} ${failedKeys.size === 1 ? "person" : "people"}`;
    message += ` The identity form could not be emailed to ${who}. Check the email address and send a reminder.`;
  }
  return message;
}

/** The allowed references on an item, in the order the engine sent them. */
function allowedRefs(item: JobStatusOpenItem): JobStatusActionRef[] {
  return item.actions.filter((ref) => ref.allowed);
}

/**
 * The allowed references that get a button, in the engine's order. Card 2404: a person-level
 * decision with nobody to act on is dropped rather than drawn as a button that cannot post; a
 * grouped item's Restore is ONE button (the dialog picks the person), however many references the
 * engine sent for it, and needs someone to pick.
 */
export function drawableRefs(item: JobStatusOpenItem): JobStatusActionRef[] {
  let restoreSeen = false;
  return allowedRefs(item).filter((ref) => {
    if (NEEDS_PARTY.has(ref.action)) {
      return (ref.target.party_key ?? item.owed_by.party_key ?? null) !== null;
    }
    if (ref.action === "restore_party") {
      if (restoreSeen || (item.people ?? []).length === 0) return false;
      restoreSeen = true;
    }
    return true;
  });
}

// ── The dialogs ──────────────────────────────────────────────────────────────────────────

/** One editable row of the look-through form. `id` is React's key, never sent. */
interface ControllerDraft {
  id: number;
  name: string;
  band: LookThroughBand;
  email: string;
}

/**
 * "Record the look-through" — the approved ResolveDialog screen. Builds EXACTLY the contract's
 * body; the page posts it.
 */
export function LookThroughDialog({
  ownerName,
  clientName,
  partyKey,
  viewer,
  pending,
  error,
  onCancel,
  onSave,
}: {
  ownerName: string;
  clientName: string;
  partyKey: string;
  viewer: string | null;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (body: RecordLookThroughRequest) => void;
}): JSX.Element {
  const nextId = useRef(1);
  const [rows, setRows] = useState<ControllerDraft[]>([
    { id: 0, name: "", band: "25_50", email: "" },
  ]);
  const [confirmedVia, setConfirmedVia] = useState<LookThroughConfirmedVia>("companies_house");
  const [notes, setNotes] = useState("");
  const [sendChecks, setSendChecks] = useState(true);
  const [problem, setProblem] = useState<string | null>(null);

  const update = (id: number, patch: Partial<ControllerDraft>): void =>
    setRows((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row)));

  const save = (): void => {
    const named = rows.map((row) => ({ ...row, name: row.name.trim(), email: row.email.trim() }));
    if (named.length === 0 || named.some((row) => row.name === "")) {
      setProblem("Add the full name of each person who owns or controls it.");
      return;
    }
    const badEmail = named.find((row) => sendChecks && row.email !== "" && !row.email.includes("@"));
    if (badEmail !== undefined) {
      setProblem(`Check the email address for ${badEmail.name}.`);
      return;
    }
    setProblem(null);
    onSave({
      party_key: partyKey,
      controllers: named.map((row) => ({
        name: row.name,
        band: row.band,
        email: sendChecks && row.email !== "" ? row.email : null,
      })),
      confirmed_via: confirmedVia,
      notes: notes.trim() === "" ? null : notes.trim(),
      send_identity_checks: sendChecks,
    });
  };

  const shown = problem ?? error;

  return (
    <Modal
      open
      onClose={onCancel}
      dismissible={!pending}
      title="Record the look-through"
      description={`${ownerName} owns 25% or more of ${clientName}. Record the people who own or control it.`}
      closeLabel="Close"
      className="modal--wide"
      footer={
        <div className="lt-foot">
          <span className="lt-hint">
            Recorded as {viewer ?? "you"}, today. This clears the approval hold.
          </span>
          <div className="lt-foot__buttons">
            <Button variant="ghost" onClick={onCancel} disabled={pending}>
              Cancel
            </Button>
            <Button variant="primary" onClick={save} disabled={pending}>
              {pending ? "Saving…" : "Save look-through"}
            </Button>
          </div>
        </div>
      }
    >
      <form
        className="lt-form"
        onSubmit={(event) => {
          event.preventDefault();
          save();
        }}
      >
        <fieldset className="lt-people">
          <legend className="lt-label">Who owns or controls it?</legend>
          {rows.map((row, index) => (
            <div key={row.id} className={`lt-person${sendChecks ? " lt-person--email" : ""}`}>
              <Input
                variant="text"
                type="text"
                aria-label={`Full name, person ${index + 1}`}
                placeholder="Full name"
                value={row.name}
                autoFocus={index === 0}
                onChange={(event) => update(row.id, { name: event.target.value })}
              />
              {sendChecks && (
                <Input
                  variant="text"
                  type="email"
                  aria-label={`Email, person ${index + 1}`}
                  placeholder="Email (optional)"
                  value={row.email}
                  onChange={(event) => update(row.id, { email: event.target.value })}
                />
              )}
              <Select
                aria-label={`Their control, person ${index + 1}`}
                options={BAND_OPTIONS}
                value={row.band}
                onValueChange={(band) => update(row.id, { band })}
              />
              <button
                type="button"
                className="lt-remove"
                aria-label={`Remove person ${index + 1}`}
                disabled={rows.length === 1}
                onClick={() => setRows((current) => current.filter((r) => r.id !== row.id))}
              >
                <Icon name="x" size={16} aria-hidden="true" />
              </button>
            </div>
          ))}
          <Button
            type="button"
            variant="ghost"
            className="lt-add"
            onClick={() =>
              setRows((current) => [
                ...current,
                { id: nextId.current++, name: "", band: "25_50", email: "" },
              ])
            }
          >
            <Icon name="plus" size={14} aria-hidden="true" />
            Add a person
          </Button>
        </fieldset>

        <label className="lt-field">
          <span className="lt-label">How did you confirm this?</span>
          <Select
            options={CONFIRMED_VIA_OPTIONS}
            value={confirmedVia}
            onValueChange={setConfirmedVia}
          />
        </label>

        <label className="lt-field">
          <span className="lt-label">
            Notes <span className="lt-optional">(optional)</span>
          </span>
          <Textarea
            rows={3}
            placeholder="What you checked and where"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </label>

        <label className="lt-check">
          <input
            type="checkbox"
            checked={sendChecks}
            onChange={(event) => setSendChecks(event.target.checked)}
          />
          <span>
            <span className="lt-check__title">Send identity checks to these people</span>
            <span className="lt-hint">
              Anyone who indirectly owns 25% or more gets their own identity form and SmartSearch
              check, like a director.
            </span>
          </span>
        </label>

        {shown !== null && (
          <p className="lt-error" role="alert">
            {shown}
          </p>
        )}
      </form>
    </Modal>
  );
}

/** A small confirm: a sentence, and one button that does it (or a link that goes there). */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  href,
  pending,
  error = null,
  onCancel,
  onConfirm,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  href?: string;
  pending: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}): JSX.Element {
  return (
    <Modal
      open
      onClose={onCancel}
      dismissible={!pending}
      title={title}
      closeLabel="Close"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          {href !== undefined ? (
            <a
              className="btn btn--primary"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onCancel}
            >
              {confirmLabel}
            </a>
          ) : (
            <Button variant="primary" onClick={onConfirm} disabled={pending} autoFocus>
              {pending ? "Working…" : confirmLabel}
            </Button>
          )}
        </>
      }
    >
      <p className="lt-confirm">{body}</p>
      {error !== null && (
        <p className="lt-error" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}

// ── Card 2404: the decisions on a person ────────────────────────────────────────────────────
//
// Every dialog below is the approved ResolveDialog screen's pattern (the walk-4 design,
// https://claude.ai/artifact/D5umzTyYDMcE3xeEqwE8vB): a title and a line under it, the body, and a
// footer that says who it is recorded as, with Cancel and the one primary button. Built from the
// design system's Modal, Input, Textarea, Select and Button, in the look-through dialog's layout
// classes, so the six read as one family.

/** The ResolveDialog shell: head, body, and a "Recorded as <you>, today." footer. */
export function ResolveDialog({
  title,
  description,
  viewer,
  note,
  primaryLabel,
  pendingLabel = "Saving…",
  pending,
  canSubmit = true,
  error,
  onCancel,
  onSubmit,
  children,
}: {
  title: string;
  description: ReactNode;
  viewer: string | null;
  /** Said after "Recorded as <you>, today." — what the act does beyond being recorded. */
  note?: string;
  primaryLabel: string;
  pendingLabel?: string;
  pending: boolean;
  canSubmit?: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: () => void;
  children: ReactNode;
}): JSX.Element {
  const submit = (): void => {
    if (canSubmit && !pending) onSubmit();
  };
  return (
    <Modal
      open
      onClose={onCancel}
      dismissible={!pending}
      title={title}
      description={description}
      closeLabel="Close"
      className="modal--wide"
      footer={
        <div className="lt-foot">
          <span className="lt-hint">
            Recorded as {viewer ?? "you"}, today.{note !== undefined ? ` ${note}` : ""}
          </span>
          <div className="lt-foot__buttons">
            <Button variant="ghost" onClick={onCancel} disabled={pending}>
              Cancel
            </Button>
            <Button variant="primary" onClick={submit} disabled={pending || !canSubmit}>
              {pending ? pendingLabel : primaryLabel}
            </Button>
          </div>
        </div>
      }
    >
      <form
        className="lt-form"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {children}
        {error !== null && (
          <p className="lt-error" role="alert">
            {error}
          </p>
        )}
      </form>
    </Modal>
  );
}

/**
 * View result — what the identity check and the screening found for one person, from the file as
 * last read (`people[]`): nothing is posted. Card 2404: the failed check's reason in the engine's
 * plain words, and each watchlist match. From here the staffer can go straight on to the item's
 * other allowed decisions (Clear, Ask them to re-run).
 */
export function ViewResultDialog({
  person,
  fallbackName,
  next,
  onClose,
}: {
  person: JobStatusPerson | undefined;
  fallbackName: string;
  /** The item's other allowed decisions, as buttons that switch to that dialog. */
  next: readonly { label: string; open: () => void }[];
  onClose: () => void;
}): JSX.Element {
  const hits = person?.screening.hits ?? [];
  return (
    <Modal
      open
      onClose={onClose}
      title="What the checks found"
      description={
        person === undefined
          ? fallbackName
          : person.role_label.trim() === ""
            ? person.name
            : `${person.name} · ${person.role_label}`
      }
      closeLabel="Close"
      className="modal--wide"
      footer={
        <div className="lt-foot">
          <span className="lt-hint">Viewing a result records nothing.</span>
          <div className="lt-foot__buttons">
            {next.map((action) => (
              <Button key={action.label} variant="ghost" onClick={action.open}>
                {action.label}
              </Button>
            ))}
            <Button variant="primary" onClick={onClose} autoFocus>
              Close
            </Button>
          </div>
        </div>
      }
    >
      {person === undefined ? (
        <p className="lt-confirm">This person&rsquo;s result is not on the file yet. Refresh to check again.</p>
      ) : (
        <div className="lt-form">
          <section className="vr-part" aria-label="Identity check">
            <span className="lt-label">Identity check</span>
            <PersonStatus status={person.id_check} />
            {person.id_check.detail !== undefined && person.id_check.detail.trim() !== "" && (
              <p className="lt-confirm">{person.id_check.detail}</p>
            )}
          </section>
          <section className="vr-part" aria-label="Screening">
            <span className="lt-label">Screening</span>
            <PersonStatus status={person.screening} />
            {hits.length > 0 && (
              <ul className="vr-hits">
                {hits.map((hit, index) => (
                  <li key={`${hit.list}-${index}`} className="lt-confirm">
                    {hitLine(hit)}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </Modal>
  );
}

/** Clear — a REQUIRED reason, never blank; the button stays off until there is one. */
export function ClearPartyDialog({
  name,
  viewer,
  pending,
  error,
  onCancel,
  onSave,
}: {
  name: string;
  viewer: string | null;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (reason: string) => void;
}): JSX.Element {
  const [reason, setReason] = useState("");
  const ready = reason.trim() !== "";
  return (
    <ResolveDialog
      title={`Clear ${name}`}
      description="The file stops waiting on them. Say why: your reason is kept in the file's history."
      viewer={viewer}
      primaryLabel="Clear them"
      pending={pending}
      canSubmit={ready}
      error={error}
      onCancel={onCancel}
      onSubmit={() => onSave(reason.trim())}
    >
      <label className="lt-field">
        <span className="lt-label">Why are you clearing them?</span>
        <Textarea
          rows={3}
          required
          autoFocus
          placeholder="What you checked, and why they do not need to hold up the file"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </label>
    </ResolveDialog>
  );
}

/**
 * Where the identity check being re-run stands: it did not pass, or the person never finished it
 * (card 2404, sign-off walk 2316). Read from the person's `id_check.state`.
 */
export type RerunCheckState = "failed" | "in_progress";

/**
 * Ask them to re-run — a confirm: a fresh identity check for someone whose check did not pass, or
 * who has not finished the one we sent.
 */
export function RerunDialog({
  name,
  state,
  viewer,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  name: string;
  state: RerunCheckState;
  viewer: string | null;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}): JSX.Element {
  const unfinished = state === "in_progress";
  return (
    <ResolveDialog
      title="Ask them to re-run the check"
      description={
        unfinished ? `${name}'s identity check has not finished.` : `${name}'s identity check did not pass.`
      }
      viewer={viewer}
      primaryLabel="Ask them to re-run"
      pendingLabel="Working…"
      pending={pending}
      error={error}
      onCancel={onCancel}
      onSubmit={onConfirm}
    >
      <p className="lt-confirm">
        This starts a fresh identity check for them.{" "}
        {unfinished ? "The check they did not finish" : "The result that did not pass"} stays in the
        file&rsquo;s history.
      </p>
    </ResolveDialog>
  );
}

/**
 * Confirm removal — the people the client removed, listed, cleared in ONE act. The engine reads who
 * they are itself; the list here is only so the staffer sees whom they are confirming.
 */
export function ConfirmRemovalDialog({
  people,
  viewer,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  people: readonly JobStatusOpenItemPerson[];
  viewer: string | null;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (reason: string | null) => void;
}): JSX.Element {
  const [notes, setNotes] = useState("");
  const count = people.length;
  return (
    <ResolveDialog
      title="Confirm the client's removals"
      description={
        count === 1
          ? "The client removed this person, who is listed at Companies House. Confirming clears them from the file's checks."
          : `The client removed these ${count} people, who are listed at Companies House. Confirming clears them all from the file's checks.`
      }
      viewer={viewer}
      primaryLabel="Confirm removal"
      pending={pending}
      error={error}
      onCancel={onCancel}
      onSubmit={() => onConfirm(notes.trim() === "" ? null : notes.trim())}
    >
      <ul className="rd-people" aria-label="People the client removed">
        {people.map((person) => (
          <li key={person.party_key}>{person.name}</li>
        ))}
      </ul>
      <label className="lt-field">
        <span className="lt-label">
          Notes <span className="lt-optional">(optional)</span>
        </span>
        <Textarea
          rows={2}
          placeholder="Anything the client told you about why"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </label>
    </ResolveDialog>
  );
}

/** Restore — pick ONE of the people the client removed and put them back on the file. */
export function RestoreDialog({
  people,
  initial,
  viewer,
  pending,
  error,
  onCancel,
  onRestore,
}: {
  people: readonly JobStatusOpenItemPerson[];
  initial: string | null;
  viewer: string | null;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onRestore: (person: JobStatusOpenItemPerson) => void;
}): JSX.Element {
  const first = people.find((person) => person.party_key === initial) ?? people[0];
  const [chosen, setChosen] = useState<string>(first?.party_key ?? "");
  const person = people.find((candidate) => candidate.party_key === chosen);
  return (
    <ResolveDialog
      title="Restore someone the client removed"
      description="They go back on the file and need an email address before their identity form can be sent."
      viewer={viewer}
      primaryLabel="Restore"
      pending={pending}
      canSubmit={person !== undefined}
      error={error}
      onCancel={onCancel}
      onSubmit={() => {
        if (person !== undefined) onRestore(person);
      }}
    >
      <label className="lt-field">
        <span className="lt-label">Who should stay on the file?</span>
        <Select
          options={people.map((candidate) => ({ value: candidate.party_key, label: candidate.name }))}
          value={chosen}
          onValueChange={setChosen}
        />
      </label>
    </ResolveDialog>
  );
}

/** Add email — one address, checked for shape here and again by the engine; it sends their form. */
export function AddEmailDialog({
  name,
  viewer,
  pending,
  error,
  onCancel,
  onSave,
}: {
  name: string;
  viewer: string | null;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (email: string) => void;
}): JSX.Element {
  const [email, setEmail] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  return (
    <ResolveDialog
      title={`Add an email address for ${name}`}
      description="We send them their identity form at this address."
      viewer={viewer}
      note="This sends their form."
      primaryLabel="Send their form"
      pendingLabel="Sending…"
      pending={pending}
      canSubmit={email.trim() !== ""}
      error={problem ?? error}
      onCancel={onCancel}
      onSubmit={() => {
        if (!looksLikeEmail(email)) {
          setProblem("Check the email address: it should look like name@example.com.");
          return;
        }
        setProblem(null);
        onSave(email.trim());
      }}
    >
      <label className="lt-field">
        <span className="lt-label">Email address</span>
        <Input
          variant="text"
          type="email"
          autoFocus
          autoComplete="off"
          placeholder="name@example.com"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            setProblem(null);
          }}
        />
      </label>
    </ResolveDialog>
  );
}

// ── Deep links ───────────────────────────────────────────────────────────────────────────

/**
 * The open item and action reference a deep link (`?action=<action>&party=<key>`) means. `party`
 * is the job_parties iteration key — the key the Chat alerts compose from `ctx.party.id`, and the
 * `party_key` every engine target and open-item key carries. Only references this viewer may take
 * (`allowed`) are candidates, tried most-specific first:
 * <ol>
 *   <li>a reference whose `target.party_key` is the key (a look-through for THAT company owner, a
 *       reminder to THAT person);</li>
 *   <li>a reference whose `target.item_key`, or an item whose `key`, is EXACTLY the key itself,
 *       `party:<key>` (their identity form) or `<item>:<key>` where `<item>` is ONE declared item
 *       id (a per-party item, `StaffItemKeys.PartyItem`) — never a loose suffix, so a bare
 *       `party=3` cannot match `party:director:3` or `personal_codes:director:3`;</li>
 *   <li>an item owed by that party;</li>
 *   <li>card 2404: a grouped item that names that party among its `people` (a Restore link for one
 *       of the people the client removed).</li>
 * </ol>
 * No `party` ⇒ the first allowed reference of that action. Nothing matches ⇒ undefined, and the
 * page just shows the file.
 */
export function findDeepLinkTarget(
  items: readonly JobStatusOpenItem[],
  action: string,
  party: string | null,
  { includeDisallowed = false }: { includeDisallowed?: boolean } = {},
): { item: JobStatusOpenItem; ref: JobStatusActionRef } | undefined {
  const candidates = items.flatMap((item) =>
    item.actions
      .filter((ref) => ref.action === action && (includeDisallowed || ref.allowed))
      .map((ref) => ({ item, ref })),
  );
  if (party === null) return candidates[0];
  const keyMatches = (key: string | undefined): boolean =>
    key !== undefined &&
    (key === party || key === `party:${party}` || isPerPartyItemKey(key, party));
  return (
    candidates.find(({ ref }) => ref.target.party_key === party) ??
    candidates.find(({ item, ref }) => keyMatches(ref.target.item_key) || keyMatches(item.key)) ??
    candidates.find(({ item }) => item.owed_by.party_key === party) ??
    candidates.find(({ item }) => (item.people ?? []).some((person) => person.party_key === party))
  );
}

/** `<item>:<party>` exactly, with `<item>` one declared item id (no `:`, not a key prefix). */
function isPerPartyItemKey(key: string, party: string): boolean {
  const suffix = `:${party}`;
  if (!key.endsWith(suffix)) return false;
  const itemId = key.slice(0, key.length - suffix.length);
  return /^[A-Za-z0-9_]+$/.test(itemId) && itemId !== "party" && itemId !== "form";
}

/** The open items a "Send reminders" (everyone) click would reach: each carries an allowed reminder. */
export function remindableItems(items: readonly JobStatusOpenItem[]): JobStatusOpenItem[] {
  return items.filter((item) =>
    item.actions.some((ref) => ref.action === "send_reminder" && ref.allowed),
  );
}

// ── The controller ───────────────────────────────────────────────────────────────────────

type OpenDialog =
  | { kind: "look_through"; item: JobStatusOpenItem; ref: JobStatusActionRef }
  | { kind: "mark_received"; item: JobStatusOpenItem; ref: JobStatusActionRef }
  | { kind: "send_reminder"; item: JobStatusOpenItem; ref: JobStatusActionRef }
  | { kind: "form_link"; item: JobStatusOpenItem; ref: JobStatusActionRef }
  | { kind: "remind_all" }
  // Card 2404 — the decisions on a person. `party` is who it is about (for Restore: who a deep
  // link asked for, else the first person listed).
  | {
      kind:
        | "view_result"
        | "clear_party"
        | "rerun_identity_check"
        | "confirm_removal"
        | "restore_party"
        | "add_email";
      item: JobStatusOpenItem;
      ref: JobStatusActionRef;
      party: string | null;
    };

interface ToastState {
  id: number;
  message: string;
  tone: "success" | "error";
}

/**
 * Everything the file page needs from the actions: per-item buttons, the panel's "Send reminders"
 * (everyone) button, a line while a deep link's item is being looked for, and the overlays.
 */
export interface FileActions {
  actionsFor: (item: JobStatusOpenItem) => ReactNode;
  panelActions: ReactNode;
  linkNotice: string | null;
  overlays: ReactNode;
}

/**
 * The file page's actions, wired to the contract's endpoints for this tenant and file. `detail` is
 * the file as last read; `viewer` is the signed-in email ("Recorded as …").
 */
export function useFileActions({
  tenantId,
  jobId,
  detail,
  viewer,
  refetch,
}: {
  tenantId: string | null;
  jobId: string | undefined;
  detail: JobStatusDetailResponse | undefined;
  viewer: string | null;
  /** Re-read the file — used while a deep link waits for its item to appear. */
  refetch?: () => void;
}): FileActions {
  const [dialog, setDialog] = useState<OpenDialog | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const toastId = useRef(0);

  const remind = useJobAction(tenantId, jobId, sendReminder);
  const lookThrough = useJobAction(tenantId, jobId, recordLookThrough);
  const received = useJobAction(tenantId, jobId, markReceived);
  // Card 2404 — the decisions on a person.
  const clear = useJobAction(tenantId, jobId, clearParty);
  const rerun = useJobAction(tenantId, jobId, rerunIdentityCheck);
  const confirmRemovals = useJobAction(tenantId, jobId, confirmRemoval);
  const restore = useJobAction(tenantId, jobId, restoreParty);
  const email = useJobAction(tenantId, jobId, addEmail);

  const say = useCallback((message: string, tone: "success" | "error"): void => {
    toastId.current += 1;
    setToast({ id: toastId.current, message, tone });
  }, []);

  const dismissToast = useCallback((): void => setToast(null), []);

  const close = useCallback((): void => {
    setDialog(null);
    setDialogError(null);
  }, []);

  /**
   * A failed action. An input-bearing dialog keeps its input unless the answer makes it moot; a
   * "only X can do this" answer stays INSIDE whatever dialog is open (a toast would take the names
   * away after a few seconds); anything else is a toast.
   */
  const failed = (error: unknown, keepDialogOpen: boolean): void => {
    const failure = describeActionError(error);
    const keep =
      (keepDialogOpen && (failure.kind === "failed" || failure.kind === "rejected")) ||
      (dialog !== null && failure.kind === "role_required");
    if (keep) {
      setDialogError(failure.message);
      return;
    }
    say(failure.message, "error");
    close();
  };

  const doRemind = (item: JobStatusOpenItem, ref: JobStatusActionRef): void => {
    sendReminderBody(reminderBody(ref));
  };

  const sendReminderBody = (body: SendReminderRequest): void => {
    setDialogError(null);
    remind.mutate(body, {
      onSuccess: (result) => {
        const said = reminderResult(result);
        say(said.message, said.ok ? "success" : "error");
        close();
      },
      onError: (error) => failed(error, false),
    });
  };

  const doMarkReceived = (item: JobStatusOpenItem, ref: JobStatusActionRef): void => {
    const body: MarkReceivedRequest = { item_key: ref.target.item_key ?? item.key };
    received.mutate(body, {
      onSuccess: () => {
        say(`Marked ${item.label} as received.`, "success");
        close();
      },
      onError: (error) => failed(error, false),
    });
  };

  const doLookThrough = (body: RecordLookThroughRequest): void => {
    setDialogError(null);
    lookThrough.mutate(body, {
      onSuccess: (result) => {
        say(lookThroughResult(result), "success");
        close();
      },
      onError: (error) => failed(error, true),
    });
  };

  // ── Card 2404: each decision posts exactly its contract body, then says what happened ──────
  const nameOf = (party: string | null, item?: JobStatusOpenItem): string => personName(detail, party, item);

  const doClear = (party: string, reason: string, item: JobStatusOpenItem): void => {
    setDialogError(null);
    clear.mutate(
      { party_key: party, reason },
      {
        onSuccess: () => {
          say(`Cleared ${nameOf(party, item)}. Your reason is in the file's history.`, "success");
          close();
        },
        onError: (error) => failed(error, true),
      },
    );
  };

  const doRerun = (party: string, item: JobStatusOpenItem): void => {
    setDialogError(null);
    rerun.mutate(
      { party_key: party },
      {
        onSuccess: () => {
          say(`A fresh identity check has started for ${nameOf(party, item)}.`, "success");
          close();
        },
        onError: (error) => failed(error, false),
      },
    );
  };

  const doConfirmRemoval = (reason: string | null, count: number): void => {
    setDialogError(null);
    confirmRemovals.mutate(reason === null ? {} : { reason }, {
      onSuccess: () => {
        say(
          count === 1
            ? "Removal confirmed. They are cleared from the file's checks."
            : `Removals confirmed. All ${count} are cleared from the file's checks.`,
          "success",
        );
        close();
      },
      onError: (error) => failed(error, true),
    });
  };

  const doRestore = (person: JobStatusOpenItemPerson): void => {
    setDialogError(null);
    restore.mutate(
      { party_key: person.party_key },
      {
        onSuccess: () => {
          say(`${person.name} is back on the file. Add their email address to send their form.`, "success");
          close();
        },
        onError: (error) => failed(error, true),
      },
    );
  };

  const doAddEmail = (party: string, address: string, item: JobStatusOpenItem): void => {
    setDialogError(null);
    email.mutate(
      { party_key: party, email: address },
      {
        onSuccess: () => {
          say(`${nameOf(party, item)}'s identity form is on its way to ${address}.`, "success");
          close();
        },
        onError: (error) => failed(error, true),
      },
    );
  };

  /** Open the right thing for a reference: a dialog, a confirm, or (for a click) the action. */
  const open = (item: JobStatusOpenItem, ref: JobStatusActionRef, fromLink: boolean): boolean => {
    if (!ref.allowed) return false;
    switch (ref.action) {
      // Card 2404: every decision on a person opens its dialog, clicked or linked alike.
      case "view_result":
      case "clear_party":
      case "rerun_identity_check":
      case "add_email": {
        const party = partyOf(item, ref);
        if (party === null) return false;
        setDialogError(null);
        setDialog({ kind: ref.action, item, ref, party });
        return true;
      }
      case "confirm_removal":
        setDialogError(null);
        setDialog({ kind: "confirm_removal", item, ref, party: null });
        return true;
      case "restore_party": {
        if ((item.people ?? []).length === 0) return false;
        const linked = fromLink ? wantedParty : null;
        setDialogError(null);
        setDialog({ kind: "restore_party", item, ref, party: ref.target.party_key ?? linked });
        return true;
      }
      case "record_look_through":
        setDialog({ kind: "look_through", item, ref });
        return true;
      case "mark_received":
        setDialog({ kind: "mark_received", item, ref });
        return true;
      case "send_reminder":
        if (fromLink) setDialog({ kind: "send_reminder", item, ref });
        else doRemind(item, ref);
        return true;
      case "start_risk_assessment":
      case "review_and_approve":
        if (ref.url === undefined) return false;
        if (fromLink) setDialog({ kind: "form_link", item, ref });
        return true;
      default:
        return false;
    }
  };

  // ── Deep links: `?action=<action>&party=<key>` opens that action's dialog once. ──────────
  const [searchParams, setSearchParams] = useSearchParams();
  const consumed = useRef(false);
  const wanted = searchParams.get("action");
  const wantedParty = searchParams.get("party");
  // Waiting for the item a link names (not open yet, or already dealt with): re-read the file.
  const [linkWaiting, setLinkWaiting] = useState(false);
  const [linkGaveUp, setLinkGaveUp] = useState(false);
  const linkNotice = linkWaiting ? DEEP_LINK_PENDING : linkGaveUp ? DEEP_LINK_GAVE_UP : null;

  const finishLink = (): void => {
    consumed.current = true;
    setLinkWaiting(false);
    // The link has done its job: take it out of the address bar so a refresh does not reopen it.
    const params = new URLSearchParams(searchParams);
    params.delete("action");
    params.delete("party");
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    if (consumed.current || wanted === null || detail === undefined) return;
    const items = detail.open_items ?? [];
    const match = findDeepLinkTarget(items, wanted, wantedParty);
    if (match !== undefined) {
      finishLink();
      open(match.item, match.ref, true);
      return;
    }
    // The item is there but this viewer may not take it: show the file, as before. Only a link
    // whose item is not on the file at all is worth waiting for.
    const present = findDeepLinkTarget(items, wanted, wantedParty, { includeDisallowed: true });
    if (present !== undefined || refetch === undefined || linkGaveUp) {
      finishLink();
      return;
    }
    setLinkWaiting(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, wanted, wantedParty, linkGaveUp]);

  useEffect(() => {
    if (!linkWaiting || refetch === undefined) return;
    const started = Date.now();
    const timer = setInterval(() => {
      if (Date.now() - started >= DEEP_LINK_WAIT_MS) {
        clearInterval(timer);
        setLinkWaiting(false);
        setLinkGaveUp(true);
        return;
      }
      refetch();
    }, DEEP_LINK_POLL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkWaiting]);

  // ── Per-item buttons ────────────────────────────────────────────────────────────────────
  const actionsFor = (item: JobStatusOpenItem): ReactNode => {
    const refs = drawableRefs(item);
    if (refs.length === 0) return null;
    // The viewer's own item leads with the ink button, as the design has it; anyone else's items
    // carry quiet ones.
    const lead = item.owed_by.kind === "staff" ? "primary" : "ghost";
    return refs.map((ref, index) => {
      const personLabel = PERSON_ACTION_LABEL[ref.action];
      if (personLabel !== undefined) {
        const variant = index === 0 ? lead : "ghost";
        return (
          <Button
            key={`${ref.action}-${index}`}
            variant={variant}
            onClick={() => open(item, ref, false)}
          >
            {personLabel}
          </Button>
        );
      }
      const variant = index === 0 ? lead : "ghost";
      const key = `${ref.action}-${index}`;
      const formLabel = FORM_LINK_LABEL[ref.action];
      if (formLabel !== undefined) {
        if (ref.url === undefined) return null;
        return (
          <a
            key={key}
            className={`btn btn--${variant}`}
            href={ref.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {formLabel}
          </a>
        );
      }
      if (ref.action === "send_reminder") {
        const single = isSingleReminder(ref);
        return (
          <Button
            key={key}
            variant={variant}
            disabled={remind.isPending}
            onClick={() => open(item, ref, false)}
          >
            {single ? "Send reminder" : "Send reminders"}
          </Button>
        );
      }
      if (ref.action === "record_look_through") {
        return (
          <Button key={key} variant={variant} onClick={() => open(item, ref, false)}>
            Record look-through
          </Button>
        );
      }
      if (ref.action === "mark_received") {
        return (
          <Button key={key} variant={variant} onClick={() => open(item, ref, false)}>
            Mark received
          </Button>
        );
      }
      return null;
    });
  };

  // ── The panel's "Send reminders": everyone owed, once two or more items can be chased ─────
  const remindable = remindableItems(detail?.open_items ?? []);
  const panelActions: ReactNode =
    remindable.length >= 2 ? (
      <Button
        variant="ghost"
        disabled={remind.isPending}
        onClick={() => setDialog({ kind: "remind_all" })}
      >
        Send reminders
      </Button>
    ) : null;

  // ── Overlays ────────────────────────────────────────────────────────────────────────────
  const clientName = detail?.client_name ?? "this client";
  let overlay: ReactNode = null;
  if (dialog?.kind === "look_through") {
    const partyKey = dialog.ref.target.party_key ?? dialog.item.owed_by.party_key ?? "";
    const owner =
      detail?.people?.find((person) => person.party_key === partyKey)?.name ??
      detail?.parties.find((party) => party.iteration_key === partyKey)?.display_name ??
      "A company";
    overlay = (
      <LookThroughDialog
        ownerName={owner}
        clientName={clientName}
        partyKey={partyKey}
        viewer={viewer}
        pending={lookThrough.isPending}
        error={dialogError}
        onCancel={close}
        onSave={doLookThrough}
      />
    );
  } else if (dialog?.kind === "mark_received") {
    const { item, ref } = dialog;
    overlay = (
      <ConfirmDialog
        title="Mark as received"
        body={`Mark ${item.label} as received? Use this when it came in outside the form. It settles the item and is recorded against your name.`}
        confirmLabel="Mark received"
        pending={received.isPending}
        error={dialogError}
        onCancel={close}
        onConfirm={() => doMarkReceived(item, ref)}
      />
    );
  } else if (dialog?.kind === "send_reminder") {
    const { item, ref } = dialog;
    const single = isSingleReminder(ref);
    overlay = (
      <ConfirmDialog
        title={single ? "Send a reminder" : "Send reminders"}
        body={
          single
            ? `Send ${item.owed_by.kind === "client" ? "the client" : item.owed_by.name} a reminder about ${item.label} now?`
            : `Send a reminder now to everyone this file is still waiting on?`
        }
        confirmLabel={single ? "Send reminder" : "Send reminders"}
        pending={remind.isPending}
        error={dialogError}
        onCancel={close}
        onConfirm={() => doRemind(item, ref)}
      />
    );
  } else if (dialog?.kind === "remind_all") {
    overlay = (
      <ConfirmDialog
        title="Send reminders"
        body={`Send a reminder now to everyone this file is still waiting on (${remindable.length} items)?`}
        confirmLabel="Send reminders"
        pending={remind.isPending}
        error={dialogError}
        onCancel={close}
        onConfirm={() => sendReminderBody({ all: true })}
      />
    );
  } else if (dialog?.kind === "form_link") {
    const label = FORM_LINK_LABEL[dialog.ref.action] ?? "Open the form";
    overlay = (
      <ConfirmDialog
        title={label}
        body={`${dialog.item.label}: this opens the staff form in a new tab.`}
        confirmLabel={label}
        href={dialog.ref.url}
        pending={false}
        onCancel={close}
        onConfirm={close}
      />
    );
  } else if (dialog?.kind === "view_result") {
    const { item, party } = dialog;
    // From the result, the item's other allowed decisions are one click away.
    const next = drawableRefs(item)
      .filter((ref) => ref.action === "clear_party" || ref.action === "rerun_identity_check")
      .map((ref) => ({
        label: PERSON_ACTION_LABEL[ref.action] ?? ref.action,
        open: () => {
          open(item, ref, false);
        },
      }));
    overlay = (
      <ViewResultDialog
        person={detail?.people?.find((person) => person.party_key === party)}
        fallbackName={nameOf(party, item)}
        next={next}
        onClose={close}
      />
    );
  } else if (dialog?.kind === "clear_party" && dialog.party !== null) {
    const { item, party } = dialog;
    overlay = (
      <ClearPartyDialog
        name={nameOf(party, item)}
        viewer={viewer}
        pending={clear.isPending}
        error={dialogError}
        onCancel={close}
        onSave={(reason) => doClear(party, reason, item)}
      />
    );
  } else if (dialog?.kind === "rerun_identity_check" && dialog.party !== null) {
    const { item, party } = dialog;
    // Card 2404 (sign-off walk 2316) — a pending check reads "has not finished", a failed one
    // "did not pass": the person's own id_check state, looked up as personName looks up their name.
    const checkState = detail?.people?.find((person) => person.party_key === party)?.id_check.state;
    overlay = (
      <RerunDialog
        name={nameOf(party, item)}
        state={checkState === "in_progress" ? "in_progress" : "failed"}
        viewer={viewer}
        pending={rerun.isPending}
        error={dialogError}
        onCancel={close}
        onConfirm={() => doRerun(party, item)}
      />
    );
  } else if (dialog?.kind === "confirm_removal") {
    const people = dialog.item.people ?? [];
    overlay = (
      <ConfirmRemovalDialog
        people={people}
        viewer={viewer}
        pending={confirmRemovals.isPending}
        error={dialogError}
        onCancel={close}
        onConfirm={(reason) => doConfirmRemoval(reason, people.length)}
      />
    );
  } else if (dialog?.kind === "restore_party") {
    overlay = (
      <RestoreDialog
        people={dialog.item.people ?? []}
        initial={dialog.party}
        viewer={viewer}
        pending={restore.isPending}
        error={dialogError}
        onCancel={close}
        onRestore={doRestore}
      />
    );
  } else if (dialog?.kind === "add_email" && dialog.party !== null) {
    const { item, party } = dialog;
    overlay = (
      <AddEmailDialog
        name={nameOf(party, item)}
        viewer={viewer}
        pending={email.isPending}
        error={dialogError}
        onCancel={close}
        onSave={(address) => doAddEmail(party, address, item)}
      />
    );
  }

  const overlays = (
    <>
      {overlay}
      {toast !== null && (
        <Toast
          key={toast.id}
          message={toast.message}
          tone={toast.tone}
          durationMs={toast.tone === "error" ? ERROR_TOAST_MS : undefined}
          onDismiss={dismissToast}
        />
      )}
    </>
  );

  return { actionsFor, panelActions, linkNotice, overlays };
}
